import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiErrorMessage, formatINR } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import BorrowerSearch from "@/components/BorrowerSearch";
import { NEW_LOAN } from "@/constants/testIds";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Sparkle, ArrowLeft, MagicWand, FloppyDisk } from "@phosphor-icons/react";

const EXAMPLES = [
  "Gave Ravi ₹50,000 for one month at 12%",
  "Lent 2 lakh to Priya (9876543210) for 3 months",
  "Sunil borrowed 15000 for 2 weeks, no interest",
];

export default function NewLoanPage() {
  const nav = useNavigate();
  const [text, setText] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    borrower_name: "",
    borrower_phone: "",
    borrower_email: "",
    amount: "",
    interest_rate: "0",
    tenure_days: "30",
    purpose: "",
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [extracted, setExtracted] = useState(false);
  const [borrowerQuery, setBorrowerQuery] = useState("");
  const [borrowerUser, setBorrowerUser] = useState(null);

  const runExtract = async () => {
    if (!text.trim()) {
      toast.error("Type a sentence first");
      return;
    }
    setExtracting(true);
    try {
      const { data } = await api.post("/loans/extract", { text });
      setForm({
        borrower_name: data.borrower_name || "",
        borrower_phone: data.borrower_phone || "",
        amount: data.amount ? String(data.amount) : "",
        interest_rate: String(data.interest_rate ?? 0),
        tenure_days: String(data.tenure_days || 30),
        purpose: data.purpose || "",
      });
      setExtracted(true);
      toast.success("Extracted with AI");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setExtracting(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.borrower_name.trim() || !form.amount) {
      toast.error("Borrower name and amount are required");
      return;
    }
    if (!acceptTerms) {
      toast.error("Please accept the LeLe Terms & Conditions");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        borrower_name: form.borrower_name.trim(),
        borrower_phone: form.borrower_phone.trim(),
        amount: parseFloat(form.amount),
        interest_rate: parseFloat(form.interest_rate) || 0,
        tenure_days: parseInt(form.tenure_days) || 30,
        purpose: form.purpose.trim(),
        accept_terms: acceptTerms,
        borrower_id: borrowerUser?.id || undefined,
        borrower_email: form.borrower_email?.trim() || "",
      };
      const { data } = await api.post("/loans", payload);
      toast.success("Loan documented");
      nav(`/loans/${data.id}`);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const preview = form.amount ? formatINR(parseFloat(form.amount) || 0) : "—";

  return (
    <div className="min-h-screen flex flex-col">
      <AppHeader />
      <main className="flex-1 mx-auto max-w-4xl px-5 md:px-8 py-8 md:py-12 w-full">
        <button
          className="text-sm text-neutral-500 hover:text-neutral-200 flex items-center gap-1.5 mb-6"
          onClick={() => nav("/")}
        >
          <ArrowLeft size={14} weight="bold" /> Back to dashboard
        </button>

        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-indigo-300 mb-2">
            Create loan
          </div>
          <h1 className="font-heading text-3xl md:text-4xl tracking-tight">
            Just describe it. AI does the rest.
          </h1>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="lele-card p-6 md:p-8 mb-8"
        >
          <label className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 flex items-center gap-2 mb-3">
            <Sparkle size={12} weight="fill" className="text-indigo-400" />
            Describe the loan
          </label>
          <textarea
            data-testid={NEW_LOAN.aiTextarea}
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            placeholder="e.g. Gave Ravi ₹50,000 for one month at 12%"
            className="lele-input w-full rounded-xl px-4 py-3 text-base resize-none"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            {EXAMPLES.map((ex, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setText(ex)}
                className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-neutral-400 hover:text-neutral-100 hover:border-white/25 transition-colors"
              >
                {ex}
              </button>
            ))}
          </div>
          <div className="mt-5 flex items-center justify-between">
            <div className="text-xs text-neutral-500">
              Powered by Claude Sonnet 4.5
            </div>
            <button
              data-testid={NEW_LOAN.aiExtractBtn}
              onClick={runExtract}
              disabled={extracting}
              className="lele-btn-primary flex items-center gap-2"
            >
              <MagicWand size={16} weight="fill" />
              {extracting ? "Extracting…" : "Extract with AI"}
            </button>
          </div>
        </motion.div>

        <motion.form
          onSubmit={save}
          initial={{ opacity: extracted ? 1 : 0.6 }}
          animate={{ opacity: 1 }}
          className="lele-card p-6 md:p-8"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500">
                Review & confirm
              </div>
              <div className="font-heading text-xl mt-1">Loan details</div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500">
                Amount
              </div>
              <div className="font-heading text-2xl text-indigo-300">
                {preview}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <BorrowerSearch
                query={borrowerQuery}
                setQuery={setBorrowerQuery}
                selected={borrowerUser}
                onSelect={(u) => {
                  setBorrowerUser(u);
                  setBorrowerQuery("");
                  setForm((f) => ({
                    ...f,
                    borrower_name: u.name || f.borrower_name,
                    borrower_phone: u.phone || f.borrower_phone,
                    borrower_email: u.email || f.borrower_email,
                  }));
                }}
                onClear={() => setBorrowerUser(null)}
                borrowerName={form.borrower_name}
                borrowerPhone={form.borrower_phone}
                borrowerEmail={form.borrower_email}
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
                Borrower name *
              </label>
              <input
                data-testid={NEW_LOAN.borrowerName}
                type="text"
                required
                className="lele-input w-full rounded-lg px-4 py-3"
                value={form.borrower_name}
                onChange={(e) => setForm({ ...form, borrower_name: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
                Borrower phone (WhatsApp)
              </label>
              <input
                data-testid={NEW_LOAN.borrowerPhone}
                type="tel"
                placeholder="9876543210"
                className="lele-input w-full rounded-lg px-4 py-3"
                value={form.borrower_phone}
                onChange={(e) => setForm({ ...form, borrower_phone: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
                Borrower email (optional — used for invites)
              </label>
              <input
                data-testid="new-loan-borrower-email"
                type="email"
                placeholder="ravi@example.com"
                className="lele-input w-full rounded-lg px-4 py-3"
                value={form.borrower_email}
                onChange={(e) => setForm({ ...form, borrower_email: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
                Amount (INR) *
              </label>
              <input
                data-testid={NEW_LOAN.amount}
                type="number"
                min="1"
                step="1"
                required
                className="lele-input w-full rounded-lg px-4 py-3"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
                  Interest (%/yr)
                </label>
                <input
                  data-testid={NEW_LOAN.interest}
                  type="number"
                  min="0"
                  step="0.1"
                  className="lele-input w-full rounded-lg px-4 py-3"
                  value={form.interest_rate}
                  onChange={(e) => setForm({ ...form, interest_rate: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
                  Tenure (days)
                </label>
                <input
                  data-testid={NEW_LOAN.tenure}
                  type="number"
                  min="1"
                  step="1"
                  className="lele-input w-full rounded-lg px-4 py-3"
                  value={form.tenure_days}
                  onChange={(e) => setForm({ ...form, tenure_days: e.target.value })}
                />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
                Purpose (optional)
              </label>
              <input
                data-testid={NEW_LOAN.purpose}
                type="text"
                className="lele-input w-full rounded-lg px-4 py-3"
                value={form.purpose}
                onChange={(e) => setForm({ ...form, purpose: e.target.value })}
              />
            </div>
          </div>

          {/* Terms acceptance */}
          <div className="mt-8 pt-6 border-t border-white/5">
            <label
              htmlFor="accept-terms"
              className="flex items-start gap-3 text-sm text-neutral-300 cursor-pointer select-none"
            >
              <input
                id="accept-terms"
                data-testid="new-loan-accept-terms"
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-white/20 bg-transparent accent-indigo-500 focus:ring-2 focus:ring-indigo-500"
              />
              <span className="leading-relaxed">
                I have read and agree to the LeLe{" "}
                <Link
                  to="/terms"
                  target="_blank"
                  className="text-indigo-300 hover:text-indigo-200 underline underline-offset-2"
                >
                  Terms &amp; Conditions
                </Link>
                . I confirm that the details above are accurate and that this is
                a private, informal loan between me and the borrower.
              </span>
            </label>
          </div>

          <div className="mt-8 flex items-center justify-end gap-3">
            <button
              data-testid={NEW_LOAN.saveBtn}
              type="submit"
              disabled={saving || !acceptTerms}
              className="lele-btn-primary flex items-center gap-2"
            >
              <FloppyDisk size={16} weight="fill" />
              {saving ? "Saving…" : "Save loan"}
            </button>
          </div>
        </motion.form>
      </main>
      <Footer />
    </div>
  );
}
